import { workspaceApiFetch } from '@affine/core/modules/cloud';
import type {
  AgentPointer,
  StoreSelectionExtension,
} from '@blocksuite/affine/store';
import { useSyncExternalStore } from 'react';

/** Whether the assistant may operate the app, and who took the choice away. */
export interface AssistantAppUse {
  enabled: boolean;
  /** A workspace rule decides instead of the user: it always asks, or never operates. */
  lock: 'asks' | 'denied' | null;
}

/** A block or canvas element the assistant changed. */
export interface AssistantTouchedPlace {
  docId: string;
  id: string;
}

/** A row of a database the assistant wrote, or one cell of it. */
export interface AssistantWorkedPlace {
  docId: string;
  databaseId: string;
  rowId: string;
  columnId?: string;
}

const MAX_TOUCHED_PLACES = 64;

export interface AssistantPresenceSnapshot {
  /** `null` until the server answered. */
  appUse: AssistantAppUse | null;
  saving: boolean;
  /** The assistant is answering or operating right now. */
  working: boolean;
  /** The assistant has the hand: what the open editor selects is its doing. */
  acting: boolean;
  /** The assistant worked since app use was last switched on or stopped. */
  active: boolean;
  /** What the assistant just changed without operating the app. */
  touched: readonly AssistantTouchedPlace[];
  /** The row of a database it wrote last, for as long as that change stays marked. */
  worked: AssistantWorkedPlace | null;
  /**
   * Everything the tools of the answers that just ended changed, once the last
   * of them is done; `null` while the assistant works and before it ever did.
   */
  settled: readonly AssistantTouchedPlace[] | null;
}

/** Whether the assistant stays where it last was, also between its answers. */
export function assistantStays(
  snapshot: Pick<AssistantPresenceSnapshot, 'appUse' | 'active'>
): boolean {
  return snapshot.appUse?.enabled === true && snapshot.active;
}

export type AssistantAppUseRequest = (
  enabled?: boolean
) => Promise<AssistantAppUse>;

type Listener = () => void;

function isAppUse(value: unknown): value is AssistantAppUse {
  const candidate = value as Partial<AssistantAppUse> | null;
  return (
    typeof candidate?.enabled === 'boolean' &&
    (candidate.lock === null ||
      candidate.lock === 'asks' ||
      candidate.lock === 'denied')
  );
}

function requestAppUse(workspaceId: string): AssistantAppUseRequest {
  return async enabled => {
    const response = await workspaceApiFetch(
      `/api/workspace/${encodeURIComponent(workspaceId)}/assistant/me/app-use`,
      enabled === undefined
        ? { credentials: 'include' }
        : {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ enabled }),
          },
      {
        workspaceId,
        category: enabled === undefined ? 'settings' : 'foreground-action',
        mode: enabled === undefined ? 'background' : 'foreground',
        priority: enabled === undefined ? 'visible' : 'foreground',
      }
    );
    if (!response.ok) throw new Error('Assistant app use is unavailable');
    const appUse: unknown = await response.json();
    if (!isAppUse(appUse)) throw new Error('Assistant app use is malformed');
    return appUse;
  };
}

/**
 * The viewer's own assistant in one workspace: whether it may operate the app
 * and whether it is working. Chats report their runs here, so the avatar in the
 * top bar and the assistant's cursor can show and stop them.
 */
export class AssistantPresenceStore {
  private readonly listeners = new Set<Listener>();
  private readonly runs = new Set<() => void>();
  private latestRequest = 0;
  private pendingSaves = 0;
  /** What tools changed since the assistant started working. */
  private readonly series = new Map<
    string,
    Map<string, AssistantTouchedPlace>
  >();
  private latestAppliedRun: string | null = null;
  /** The documents an editor shows the assistant in, with how many editors do. */
  private readonly editors = new Map<string, number>();
  private snapshot: AssistantPresenceSnapshot = {
    appUse: null,
    saving: false,
    working: false,
    acting: false,
    active: false,
    touched: [],
    worked: null,
    settled: null,
  };

  constructor(private readonly request: AssistantAppUseRequest) {}

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): AssistantPresenceSnapshot => this.snapshot;

  /** Reads the switch from the server; a newer read or write wins. */
  async refresh(): Promise<void> {
    const request = ++this.latestRequest;
    const appUse = await this.request();
    if (request === this.latestRequest) this.patch({ appUse });
  }

  /** Switches app use; the switch only moves once the server stored it. */
  async setAppUse(enabled: boolean): Promise<void> {
    const request = ++this.latestRequest;
    this.pendingSaves += 1;
    this.patch({ saving: true });
    try {
      const appUse = await this.request(enabled);
      if (request === this.latestRequest)
        this.patch(appUse.enabled ? { appUse } : { appUse, active: false });
    } finally {
      this.pendingSaves -= 1;
      this.patch({ saving: this.pendingSaves > 0 });
    }
  }

  /** A chat started answering. Returns the call that ends this run. */
  beginRun(stop: () => void): () => void {
    this.runs.add(stop);
    this.patch({ working: true, active: true, settled: null });
    return () => {
      if (!this.runs.delete(stop)) return;
      if (this.runs.size > 0) return;
      // A finished assistant holds nothing; what it selected is the user's.
      this.patch({
        working: false,
        acting: false,
      });
    };
  }

  /** The assistant operates the app: from here on the editor's selection is its doing. */
  takeHand(): void {
    if (this.snapshot.working && !this.snapshot.acting)
      this.patch({ acting: true });
  }

  /** The user acts in the editor again. */
  returnHand(): void {
    if (this.snapshot.acting) this.patch({ acting: false });
  }

  bindDocument(
    docId: string,
    selection: StoreSelectionExtension,
    ownerUserId: string
  ): () => void {
    let previous = new Map<string, AgentPointer>();
    const update = (pointers: ReadonlyMap<string, AgentPointer>) => {
      for (const [key, pointer] of pointers) {
        if (
          pointer.ownerUserId !== ownerUserId ||
          previous.get(key) === pointer
        )
          continue;
        this.recordApplied(docId, pointer);
      }
      for (const [key, pointer] of previous) {
        if (pointer.ownerUserId === ownerUserId && !pointers.has(key))
          this.endAppliedRun(pointer.ownerUserId, pointer.runId);
      }
      previous = new Map(pointers);
    };
    update(selection.agentPointers);
    const subscription = selection.slots.agentPointersChanged.subscribe(update);
    return () => subscription.unsubscribe();
  }

  recordApplied(docId: string, pointer: AgentPointer): void {
    if (pointer.targets.length === 0) return;
    const key = JSON.stringify([pointer.ownerUserId, pointer.runId]);
    this.latestAppliedRun = key;
    const series =
      this.series.get(key) ?? new Map<string, AssistantTouchedPlace>();
    this.series.set(key, series);
    const touched: AssistantTouchedPlace[] = [];
    let worked: AssistantWorkedPlace | null = null;
    for (const target of pointer.targets) {
      const place = { docId, id: target.id };
      const placeKey = JSON.stringify([docId, target.id]);
      if (!touched.some(current => current.id === target.id))
        touched.push(place);
      if (series.has(placeKey) || series.size < MAX_TOUCHED_PLACES)
        series.set(placeKey, place);
      if (target.kind === 'block' && target.rowId)
        worked = {
          docId,
          databaseId: target.id,
          rowId: target.rowId,
          ...(target.columnId ? { columnId: target.columnId } : {}),
        };
      if (touched.length === MAX_TOUCHED_PLACES) break;
    }
    this.patch({ touched, worked, settled: null, active: true });
  }

  endAppliedRun(ownerUserId: string, runId: string): void {
    const key = JSON.stringify([ownerUserId, runId]);
    const series = this.series.get(key);
    if (!series) return;
    this.series.delete(key);
    if (this.latestAppliedRun === key) {
      this.latestAppliedRun = null;
      this.patch({ settled: [...series.values()], touched: [], worked: null });
    } else {
      this.patch({ settled: [...series.values()] });
    }
  }

  /**
   * An editor shows the assistant in this document: where it works there is
   * the editor's to say. Returns the call that ends it.
   */
  openEditor(docId: string): () => void {
    this.editors.set(docId, (this.editors.get(docId) ?? 0) + 1);
    this.notify();
    let open = true;
    return () => {
      if (!open) return;
      open = false;
      const left = (this.editors.get(docId) ?? 1) - 1;
      if (left > 0) this.editors.set(docId, left);
      else this.editors.delete(docId);
      this.notify();
    };
  }

  /** Whether an editor shows the assistant in this document. */
  shownIn = (docId: string): boolean => this.editors.has(docId);

  /** Stops every running chat of this workspace. */
  stop(): void {
    for (const stop of [...this.runs]) stop();
    this.patch({ active: false });
  }

  private patch(patch: Partial<AssistantPresenceSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

export function useAssistantPresence(
  store: AssistantPresenceStore
): AssistantPresenceSnapshot {
  return useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot
  );
}

const stores = new Map<string, AssistantPresenceStore>();

export function getAssistantPresenceStore(
  workspaceId: string
): AssistantPresenceStore {
  let store = stores.get(workspaceId);
  if (!store) {
    store = new AssistantPresenceStore(requestAppUse(workspaceId));
    stores.set(workspaceId, store);
  }
  return store;
}
